import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, MessageCircle, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, waLink, PHONE_TEL, PHONE_DISPLAY } from "../lib/api";
import { waQuoteText } from "../lib/i18n";
import { useLang } from "../lib/i18n";
import { FadeIn } from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Quote() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setService = (v) => setForm({ ...form, service: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error(t.booking.required);
      return;
    }
    setSending(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      window.open(waLink(waQuoteText(lang, form)), "_blank");
    } catch (err) {
      toast.error(t.common.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pt-[72px] min-h-screen bg-mist/60" data-testid="quote-page">
      <div className="max-w-2xl mx-auto px-6 py-14 md:py-20">
        {!done ? (
          <>
            <FadeIn>
              <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold text-forest-950">{t.quote.title}</h1>
              <p className="mt-3 text-body text-lg">{t.quote.sub}</p>
            </FadeIn>
            <FadeIn delay={0.1}>
              <form onSubmit={submit} className="mt-10 bg-white rounded-[28px] border border-forest-100 shadow-sm p-7 md:p-10 space-y-5" data-testid="quote-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-semibold text-forest-900">{t.quote.name} *</label>
                    <Input className="mt-2" value={form.name} onChange={set("name")} placeholder={t.booking.namePh} data-testid="quote-name-input" />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-forest-900">{t.quote.phone} *</label>
                    <Input className="mt-2" value={form.phone} onChange={set("phone")} placeholder={t.booking.phonePh} data-testid="quote-phone-input" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-forest-900">{t.quote.email}</label>
                  <Input className="mt-2" type="email" value={form.email} onChange={set("email")} placeholder={t.booking.emailPh} data-testid="quote-email-input" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-forest-900">{t.quote.service} *</label>
                  <Select value={form.service} onValueChange={setService}>
                    <SelectTrigger className="mt-2" data-testid="quote-service-select"><SelectValue placeholder={t.quote.servicePh} /></SelectTrigger>
                    <SelectContent>
                      {["household-maid", "laundry", "gardening", "pest-control", "car-valeting", "building-maintenance"].map((s) => (
                        <SelectItem key={s} value={s} data-testid={`quote-service-${s}`}>
                          {s === "household-maid" ? "Household Maid / Femme de ménage" : s === "laundry" ? "Laundry / Lessive" : s === "gardening" ? "Gardening / Jardinage" : s === "pest-control" ? "Pest Control / Antiparasitaire" : s === "car-valeting" ? "Car Valeting / Lavage auto" : "Building Maintenance / Entretien bâtiments"}
                        </SelectItem>
                      ))}
                      <SelectItem value="other" data-testid="quote-service-other">{lang === "fr" ? "Autre" : "Other"}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-semibold text-forest-900">{t.quote.message} *</label>
                  <Textarea className="mt-2" rows={5} value={form.message} onChange={set("message")} placeholder={t.quote.messagePh} data-testid="quote-message-input" />
                </div>
                <Button type="submit" disabled={sending} data-testid="quote-submit-btn" className="w-full rounded-full bg-forest-700 hover:bg-forest-800 py-4 h-auto text-white text-base font-semibold">
                  {t.quote.submit}
                </Button>
              </form>
            </FadeIn>
            <FadeIn delay={0.15}>
              <p className="mt-6 text-center text-sm text-muted-foreground">
                {lang === "fr" ? "Vous préférez appeler ? " : "Prefer to call? "}
                <a href={`tel:${PHONE_TEL}`} className="font-semibold text-forest-700 hover:text-forest-900" data-testid="quote-phone-link">{PHONE_DISPLAY}</a>
              </p>
            </FadeIn>
          </>
        ) : (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="mt-20 text-center py-10" data-testid="quote-success">
            <div className="mx-auto w-16 h-16 rounded-full bg-mint flex items-center justify-center">
              <CheckCircle2 size={34} className="text-forest-600" />
            </div>
            <h2 className="mt-5 font-display text-3xl font-bold text-forest-950">{t.quote.done}</h2>
            <p className="mt-2 text-body max-w-md mx-auto">{t.quote.doneSub}</p>
            <a href={waLink(waQuoteText(lang, form))} target="_blank" rel="noopener noreferrer" data-testid="quote-wa-continue"
              className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-8 py-4 font-semibold text-forest-950 shadow-lg hover:-translate-y-0.5 transition-all">
              <MessageCircle size={19} /> {t.quote.wa}
            </a>
            <div className="mt-5">
              <a href={`tel:${PHONE_TEL}`} className="inline-flex items-center gap-2 text-sm font-semibold text-forest-800 hover:text-forest-600" data-testid="quote-success-phone">
                <Phone size={15} /> {PHONE_DISPLAY}
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}